import React, { useEffect, useState } from 'react';
import { Box, Divider, FormControlLabel, Checkbox, Grid2 as Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { PostTypes, Steps, PostStatuses, PostPhaseStatuses } from '../../../../../../Utils/Types';
import { EmptyComing, EmptyDefault, EmptyOngoing } from './EmptyStates';
import { getStatusFilterForStep, isImplementationStep } from '../../../../../../Utils/Steps';
import TogglePostsView from './TogglePostsView';
import TabPostsList from './TabPostsList';
import TabPostsCarousel from './TabPostsCarousel';
import TabPostsMap from './TabPostsMap';
import Stats from './Stats';
import Filter from '../../../../../Dashboard/Tables/Filter';
import useWidth from '../../../../../../Hooks/useWidth';
import { FILTER_QUESTION_PREFIX } from '../../../../../../Utils/filters';
import Search from '../../../../../Dashboard/Tables/Search';
import useSpace from '../../../../../../Hooks/useSpace';
import useCurrentUser from '../../../../../../Hooks/useCurrentUser';

function TabContent({ post, stepTab }: any) {
  const theme = useTheme();
  const isMobile = useWidth('down', 'sm');
  const intl = useIntl();
  const { isLoggedIn } = useCurrentUser();
  const { space } = useSpace();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = JSON.parse(searchParams.get('filter') || '{}');

  const [statusFilterForStep, setStatusFilterForStep] = useState(
    getStatusFilterForStep(post.type, stepTab),
  );
  const defaultView = post.display_options.children_default_view;

  const [postsView, setPostsView] = useState(defaultView);
  const [iVotedFor, setIVotedFor] = useState(false);
  const showPosts = stepTab?.dates_status !== PostPhaseStatuses.COMING;
  const showSeeVotesOnly =
    isLoggedIn &&
    [Steps[post.type].VOTE, Steps[post.type].IMPLEMENTATION].includes(stepTab.step_type);

  const filterByQuestions =
    post?.form_questions?.filter((question: any) => question.public_filter_enabled) || [];

  useEffect(() => {
    setStatusFilterForStep(getStatusFilterForStep(post.type, stepTab));
    setIVotedFor(false);

    // delete the search params if there are any
    if (searchParams.toString()) {
      setSearchParams({});
    }
  }, [stepTab.step_type]);

  const renderEmptyState = () => {
    let emptyState = <EmptyDefault />;

    if (stepTab.dates_status === PostPhaseStatuses.COMING) {
      emptyState = <EmptyComing activeStep={stepTab} />;
    }
    if (stepTab.dates_status === PostPhaseStatuses.ONGOING) {
      emptyState = <EmptyOngoing space={space} post={post} activeStep={stepTab} />;
    }

    return (
      <Box
        sx={{
          pb: 3,
          px: { xs: 0, sm: 2 },
        }}
      >
        {emptyState}
      </Box>
    );
  };

  const getAllStatusItems = () => {
    const statusesToShow = statusFilterForStep || Object.values(PostStatuses);

    return statusesToShow.map((status: any) => ({
      value: status,
      label: intl.formatMessage({
        id: `DASHBOARD.FILTERS.STATUS.${status.toUpperCase()}`,
      }),
    }));
  };

  const renderFilters = () => (
    <>
      <Box sx={{ px: 2 }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Box>
              <Search />
            </Box>
          </Grid>
          <Grid sx={{ ml: 'auto' }}>
            <Box>
              <TogglePostsView
                post={post}
                postsView={postsView}
                setPostsView={setPostsView}
                defaultView={defaultView}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          px: 2,
          display: { md: 'flex' },
        }}
      >
        <Grid
          container
          spacing={2}
          wrap="wrap"
          sx={{
            justifyItems: 'center',
            width: '100%',
            my: 2,
          }}
        >
          {post.type === PostTypes.IDEAS_REQUEST && (
            <Grid size={{ md: 4 }}>
              <Filter
                name="status"
                items={getAllStatusItems()}
                label={intl.formatMessage({ id: 'DASHBOARD.FILTERS.STATUS.LABEL' })}
                placeholder={intl.formatMessage({ id: 'DASHBOARD.FILTERS.STATUS.PLACEHOLDER' })}
              />
            </Grid>
          )}
          {filterByQuestions.map((question: any) => (
            <Grid key={question.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <Filter
                name={`${FILTER_QUESTION_PREFIX}${question.id}`}
                label={question.title}
                items={question.question_options.map((option: any) => ({
                  value: option.id,
                  label:
                    option.label ||
                    intl.formatMessage({ id: 'POST_FORM.FORM_BUILDER.OPTIONS.OTHER' }),
                }))}
                placeholder={question.title}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {showSeeVotesOnly && (
        <Box
          sx={{
            pb: 3,
            px: { xs: 1, sm: 2 },
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <FormControlLabel
            sx={{
              marginTop: '6px',
              [theme.breakpoints.down('sm')]: {
                marginTop: 0,
                order: 2,
                flexBasis: '100%',
              },
            }}
            control={
              <Checkbox
                checked={iVotedFor}
                color="primary"
                onChange={(e: any) => {
                  setIVotedFor(e.target.checked);
                }}
                name="votes"
              />
            }
            label={<FormattedMessage id="POSTS.CONSULTATIONS.SEE_OWN_VOTES" />}
          />
        </Box>
      )}
    </>
  );

  return showPosts ? (
    <>
      {isImplementationStep(stepTab.step_type) && <Stats post={post} showTitle />}

      {!isMobile && (
        <Box sx={{ mb: 3 }}>
          <Divider />
        </Box>
      )}

      {renderFilters()}

      {postsView === 'carousel' && (
        <TabPostsCarousel
          parent={post}
          space={space}
          stepTab={stepTab}
          status={filter.status || statusFilterForStep}
          setPostsView={setPostsView}
          emptyState={renderEmptyState()}
          defaultView={defaultView}
        />
      )}

      {postsView === 'list' && (
        <TabPostsList
          parent={post}
          space={space}
          stepTab={stepTab}
          status={filter.status || statusFilterForStep}
          iVotedFor={iVotedFor}
          emptyState={renderEmptyState()}
        />
      )}

      {postsView === 'map' && (
        <TabPostsMap
          parentId={post.id}
          space={space}
          iVotedFor={iVotedFor}
          status={filter.status || statusFilterForStep}
          postsCount={post.post_contributions_stats?.participations_count}
          geojson={post.geojson}
        />
      )}
    </>
  ) : (
    renderEmptyState()
  );
}

export default TabContent;
